import React, { useEffect, useState, useMemo, useRef } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { Search } from 'lucide-react';
import "../../styles/components/loader.css";
import { MorphingStatusLoader } from "./MorphingStatusLoader";

// ── Base Skeleton Component ──────────────────────────────────────────────────
export const Skeleton = ({
  variant = "rectangle",
  width,
  height,
  className = "",
  style = {}
}) => {
  const baseClass = `skeleton-base skeleton-${variant} ${className}`;

  const customStyles = {
    ...style,
    ...(width && { width }),
    ...(height && { height })
  };

  return <div className={baseClass} style={customStyles} aria-hidden="true" />;
};

// ── Atomic Skeletons ────────────────────────────────────────────────────────
Skeleton.Circle = (props) => <Skeleton variant="avatar" {...props} />;
Skeleton.Text = ({ lines = 1, ...props }) => {
  return (
    <div className="skeleton-stack">
      {[...Array(lines)].map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 && lines > 1 ? "60%" : "100%"}
          {...props}
        />
      ))}
    </div>
  );
};
Skeleton.Title = (props) => <Skeleton variant="title" {...props} />;
Skeleton.Chip = (props) => <Skeleton variant="chip" {...props} />;

// ── High-Fidelity Silhouette Compositions ── (DECOMISSIONED)

/**
 * AppLoader (Animated) for initial boot or major transitions.
 * Refactored from the animated search-path component.
 */
export const AppLoader = ({ numCards = 6 }) => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [isDark, setIsDark] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    const checkTheme = () => {
      const theme = localStorage.getItem("theme") || "light";
      if (theme === "dark") return true;
      if (theme === "auto" && typeof window !== "undefined") {
        return window.matchMedia("(prefers-color-scheme: dark)").matches;
      }
      return false;
    };
    setIsDark(checkTheme());
    
    const matcher = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => setIsDark(checkTheme());
    matcher.addEventListener("change", handleChange);
    
    // Also observe local storage if another tab changes it, though mostly to handle auto
    return () => matcher.removeEventListener("change", handleChange);
  }, []);

  const config = useMemo(() => {
    const isMobile = windowWidth < 768; // Standardized with AppLayout
    const cols = windowWidth >= 1024 ? 3 : windowWidth >= 768 ? 2 : 1;
    const effectiveNumCards = isMobile ? 2 : numCards;
    const rows = Math.ceil(effectiveNumCards / cols);
    return { numCards: effectiveNumCards, cols, rows, isMobile };
  }, [windowWidth, numCards]);

  const generateSearchPath = (cfg) => {
    const { cols, rows, numCards: count } = cfg;
    let positions = [];
    const marginX = 15;
    const marginY = 15;
    const availableX = 70;
    const availableY = 70;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if ((r * cols + c) < count) {
          positions.push({
            x: `${marginX + (c * (availableX / Math.max(1, cols - 1)))}%`,
            y: `${marginY + (r * (availableY / Math.max(1, rows - 1)))}%`
          });
        }
      }
    }

    const shuffled = [...positions].sort(() => Math.random() - 0.5).slice(0, 4);
    if (shuffled.length > 0) shuffled.push(shuffled[0]);

    return {
      left: shuffled.map(p => p.x),
      top: shuffled.map(p => p.y),
      transition: {
        duration: shuffled.length * 2.5,
        repeat: Infinity,
        ease: [0.4, 0, 0.2, 1],
        times: shuffled.map((_, i) => i / (shuffled.length - 1))
      }
    };
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isDark) {
      controls.start(generateSearchPath(config));
    }
  }, [config, controls, isDark]);

  if (isDark) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', minHeight: '200px' }}>
        <MorphingStatusLoader accentColor="#16A34A" />
      </div>
    );
  }

  // Loading Animation
  return (
    <motion.div
      className="animated-skeleton-frame"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="animated-skeleton-inner">
        <motion.div className="search-icon-animator" animate={controls}>
          <motion.div
            className="search-icon-glow"
            animate={{
              boxShadow: [
                "0 0 20px rgba(59, 130, 246, 0.2)",
                "0 0 35px rgba(59, 130, 246, 0.4)",
                "0 0 20px rgba(59, 130, 246, 0.2)"
              ],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
          >
            <Search size={24} />
          </motion.div>
        </motion.div>

        <div className="skeleton-grid">
          {[...Array(config.numCards)].map((_, i) => (
            <motion.div
              key={i}
              className="skeleton-card-item"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              whileHover={{ scale: 1.02 }}
            >
              <motion.div
                className="skeleton-media-placeholder"
              />
              <div className="skeleton-stack">
                <motion.div
                  className="skeleton-text-line medium"
                />
                <motion.div
                  className="skeleton-text-line short"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

let globalInitialLoadComplete = false;

export const PageLoader = ({ loading = false, children }) => {
  const [showLoader, setShowLoader] = useState(loading);
  const [isDark, setIsDark] = useState(false);
  const startTime = useRef(loading ? Date.now() : 0);

  useEffect(() => {
    const checkTheme = () => {
      const theme = localStorage.getItem("theme") || "light";
      if (theme === "dark") return true;
      if (theme === "auto" && typeof window !== "undefined") {
        return window.matchMedia("(prefers-color-scheme: dark)").matches;
      }
      return false;
    };
    setIsDark(checkTheme());
    
    if (typeof window !== "undefined" && window.matchMedia) {
      const matcher = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => setIsDark(checkTheme());
      matcher.addEventListener("change", handleChange);
      return () => matcher.removeEventListener("change", handleChange);
    }
  }, []);

  useEffect(() => {
    let timer;
    
    // Only enforce the 3000ms full morph animation once globally.
    // Subsequent loads will have 0 delay, immediately showing content when ready.
    const minDelay = (isDark && !globalInitialLoadComplete) ? 3000 : 0;

    if (loading) {
      if (!showLoader) {
        setShowLoader(true);
        startTime.current = Date.now();
      }
    } else {
      if (showLoader) {
        const elapsed = Date.now() - startTime.current;
        if (elapsed < minDelay) {
          timer = setTimeout(() => {
            setShowLoader(false);
            globalInitialLoadComplete = true;
          }, minDelay - elapsed);
        } else {
          setShowLoader(false);
          globalInitialLoadComplete = true;
        }
      }
    }
    return () => clearTimeout(timer);
  }, [loading, isDark, showLoader]);

  return (
    <>
      <AnimatePresence>
        {showLoader && (
          <motion.div
            key="page-loader-overlay"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--surface-0)',
            }}
          >
            <AppLoader />
          </motion.div>
        )}
      </AnimatePresence>
      <motion.div
        key="page-content"
        animate={{ opacity: showLoader ? 0 : 1 }}
        transition={{ duration: 0.6, ease: "easeInOut", delay: showLoader ? 0 : 0.2 }}
        style={{ width: '100%', height: '100%', pointerEvents: showLoader ? 'none' : 'auto' }}
      >
        {children}
      </motion.div>
    </>
  );
};
